/**
 * Presentational thin div/flexbox parts for the data table, driven by the
 * `useDataTable` return (`table`/`virtualizer`/`rows`) — no engine coupling.
 *
 * Every part forwards `ref`, merges `className`, and spreads the rest of its HTML
 * attributes (`role`/`style`/`data-*`/`aria-*`/handlers). The optional
 * `pinned`/`colIndex`/`sort`/`active`/`selected` props are additive convenience
 * mappings that emit the right attribute set; the raw passthrough wins on conflict.
 */
import type { CSSProperties, HTMLAttributes, Ref } from 'react'

import { Checkbox } from '@gto/ui/checkbox'
import { cn } from '@gto/ui/cn'

import { type SortDirection, toAriaSort } from './aria-sort'
import {
  DATA_TABLE_HEADER_HEIGHT,
  DATA_TABLE_ROW_HEIGHT,
  type DataTableAlign,
  dataTableBodyVariants,
  dataTableCellVariants,
  type DataTableDensity,
  type DataTableDivider,
  dataTableHeaderCellVariants,
  dataTableHeaderVariants,
  dataTableRowVariants,
  type DataTableSurface,
  DEFAULT_DATA_TABLE_DENSITY,
} from './data-table.styles'
import type { PinnedCellProps } from './pinning.styles'

export type TableHeaderProps = HTMLAttributes<HTMLDivElement> & {
  ref?: Ref<HTMLDivElement>
  density?: DataTableDensity
  /** The header band's OWN elevation. Omit for the DS default (`elevation3`). */
  surface?: DataTableSurface
  /** Bound a grow-column header to the scrollport instead of its max-content width. */
  fitScrollport?: boolean
  /** When set, emits `role="row"` + `aria-rowindex`; pass `1` — the header is row 1 by WAI-ARIA grid convention. */
  rowIndex?: number
}

const TableHeader = ({
  className,
  density,
  surface,
  fitScrollport,
  rowIndex,
  style,
  ref,
  ...props
}: TableHeaderProps) => {
  const ariaProps =
    rowIndex === undefined ? {} : { role: 'row' as const, 'aria-rowindex': rowIndex }

  return (
    <div
      ref={ref}
      className={cn(dataTableHeaderVariants({ surface }), className)}
      // Inline height from the single source; caller `style` merges on top.
      style={{
        height: DATA_TABLE_HEADER_HEIGHT[density ?? DEFAULT_DATA_TABLE_DENSITY],
        ...style,
        ...(fitScrollport === true ? { width: '100%', minWidth: 0 } : {}),
      }}
      {...ariaProps}
      {...props}
    />
  )
}

/**
 * Merge a `pinned` payload into a cell's `style`/`className`. Caller's inline
 * `style` (usually the column width) composes over the sticky
 * `position`/`left`/`right`/`zIndex` so an explicit width wins; `className` via `cn`.
 */
const mergePinned = (
  pinned: PinnedCellProps | undefined,
  style: CSSProperties | undefined,
  className: string | undefined,
  baseClassName: string,
): { className: string; style: CSSProperties | undefined; dataAttrs: Record<string, string> } => {
  if (pinned === undefined) {
    return { className: cn(baseClassName, className), style, dataAttrs: {} }
  }

  const { className: pinnedClassName, style: pinnedStyle, ...dataAttrs } = pinned

  return {
    className: cn(baseClassName, pinnedClassName, className),
    style: { ...pinnedStyle, ...style },
    dataAttrs,
  }
}

export type TableHeaderCellProps = HTMLAttributes<HTMLDivElement> & {
  ref?: Ref<HTMLDivElement>
  align?: DataTableAlign
  /**
   * Sticky-pin payload from `getPinnedCellProps(offsets, id, {variant:'header'})`.
   * Optional + additive: omit for an unpinned column. Its sticky `style` composes
   * under the caller's inline `style`, its opaque-surface class via `cn`.
   */
  pinned?: PinnedCellProps
  /** 1-based visible-column index; emits `role="columnheader"` + `aria-colindex`. */
  colIndex?: number
  /** Sort direction (from the preset's `resolveSortDirection`); emits `aria-sort`. */
  sort?: SortDirection
}

const TableHeaderCell = ({
  align,
  className,
  colIndex,
  pinned,
  sort,
  style,
  ref,
  ...props
}: TableHeaderCellProps) => {
  const merged = mergePinned(pinned, style, className, dataTableHeaderCellVariants({ align }))

  const ariaProps = {
    ...(colIndex === undefined ? {} : { role: 'columnheader' as const, 'aria-colindex': colIndex }),
    ...(sort === undefined ? {} : { 'aria-sort': toAriaSort(sort) }),
  }

  return (
    <div
      ref={ref}
      className={merged.className}
      style={merged.style}
      {...merged.dataAttrs}
      {...ariaProps}
      {...props}
    />
  )
}

export type TableBodyProps = HTMLAttributes<HTMLDivElement> & {
  ref?: Ref<HTMLDivElement>
  /** Windowing: pass `virtualizer.getTotalSize()` to make this the full-height spacer rows translate within; omit for the unwindowed path. */
  totalSize?: number
}

const TableBody = ({ className, style, totalSize, ref, ...props }: TableBodyProps) => {
  const bodyStyle: CSSProperties | undefined =
    totalSize == null ? style : { ...style, height: totalSize }

  return (
    <div
      ref={ref}
      className={cn(dataTableBodyVariants(), className)}
      style={bodyStyle}
      {...props}
    />
  )
}

export type TableRowProps = HTMLAttributes<HTMLDivElement> & {
  ref?: Ref<HTMLDivElement>
  density?: DataTableDensity
  /** Hover/cursor affordance only; interaction wiring stays the consumer's. */
  interactive?: boolean
  selected?: boolean
  /** `false` for chrome bands (the summary band): no hover tint — not a data row. */
  hoverTint?: boolean
  /**
   * Row-separator model — see `DataTableDivider`. Anything other than `false` makes rows
   * contiguous, and the preset drops the windowed slot gap to 0 in lockstep. Defaults to `false`.
   */
  divider?: DataTableDivider
  /**
   * Whether this is the LOGICALLY last row of the dataset, which suppresses the trailing
   * separator / gap so it never doubles the card's bottom edge. Omit — the default — to fall
   * back to `:last-child`, which is right for a table whose every row is in flow and WRONG
   * under windowing, where `:last-child` is the bottom of the mounted slice. See the `last`
   * variant in `dataTableRowVariants`.
   */
  last?: boolean
  /**
   * LOGICAL `aria-rowindex` from `getAriaRowIndex(logicalIndex)` — NOT
   * `virtualItem.index` or DOM position, so a windowed row announces its true
   * position. Emits `role="row"` + `aria-rowindex`.
   */
  rowIndex?: number
  /** Bound a grow-column row to the scrollport instead of its max-content width. */
  fitScrollport?: boolean
}

const TableRow = ({
  className,
  density,
  interactive,
  divider,
  last,
  rowIndex,
  fitScrollport,
  selected,
  hoverTint,
  style,
  ref,
  ...props
}: TableRowProps) => {
  const ariaProps =
    rowIndex === undefined ? {} : { role: 'row' as const, 'aria-rowindex': rowIndex }

  return (
    <div
      ref={ref}
      className={cn(
        dataTableRowVariants({ interactive, selected, divider, hoverTint, last }),
        className,
      )}
      // Inline height from the single source `estimateSize` also reads, so the
      // windowed slot and rendered row can't drift; caller `style` (position/transform)
      // merges on top and never sets height.
      style={{
        height: DATA_TABLE_ROW_HEIGHT[density ?? DEFAULT_DATA_TABLE_DENSITY],
        ...style,
        ...(fitScrollport === true ? { width: '100%', minWidth: 0 } : {}),
      }}
      {...ariaProps}
      {...props}
    />
  )
}

export type TablePlaceholderRowProps = {
  ref?: Ref<HTMLDivElement>
  density?: DataTableDensity
  /** Match the loaded rows' `divider` model so a sparse slot's separator lines up. */
  divider?: DataTableDivider
  /**
   * Whether this placeholder occupies the body's FINAL slot — see `TableRow['last']`. It has to
   * be told, exactly as a real row does: a lookahead placeholder past the loaded frontier is the
   * body's bottom edge on every scroll-to-bottom, so leaving it to `:last-child` would suppress
   * whichever placeholder happens to be mounted last instead.
   */
  last?: boolean
  /** Position/transform for the windowed slot. */
  style?: CSSProperties
  /** Match grow-column rows so a virtual placeholder fills the same scrollport width. */
  fitScrollport?: boolean
}

/**
 * Windowed placeholder for a not-yet-loaded sparse slot. `aria-hidden` because an
 * unloaded row has no semantic content — it stays out of the grid a11y tree while
 * the loaded rows keep their real `aria-rowindex` and `aria-rowcount` announces the
 * true total. Same per-density height as a real row so the virtualizer's spacer +
 * `scrollToIndex` math stay exact.
 */
/** 2px of breathing room above and below the placeholder bar within its row slot. */
const PLACEHOLDER_BAR_VERTICAL_INSET_PX = 4

const TablePlaceholderRow = ({
  density,
  divider,
  last = false,
  style,
  fitScrollport,
  ref,
}: TablePlaceholderRowProps) => (
  <div
    ref={ref}
    aria-hidden
    // Defaults to `false`, never `auto`: a placeholder only ever exists in the SPARSE windowed
    // body, where `:last-child` is the bottom of the mounted slice rather than the body's end.
    className={cn(dataTableRowVariants({ divider, last }), 'items-center px-3')}
    data-table-row="placeholder"
    style={{
      height: DATA_TABLE_ROW_HEIGHT[density ?? DEFAULT_DATA_TABLE_DENSITY],
      ...style,
      ...(fitScrollport === true ? { width: '100%', minWidth: 0 } : {}),
    }}
  >
    {/* A static muted bar, not an animated skeleton: a windowed grid mounts/unmounts
        many of these on every scroll frame, and a per-row motion animation both costs
        frames and, under jsdom, drives motion's keyframe measurement into the
        unimplemented `window.scrollTo`. FULL-width, like the initial-load overlay's bars —
        the two loading surfaces must read as one system (QA: a 40%-wide scroll placeholder
        beside a full-width refresh skeleton read as two different UIs). Height derives from
        the density (a fixed bar overflowed every row below `standard` — the slot is
        absolutely positioned with no overflow clip); the 4px inset yields the QA-approved
        36px bar at the standard 40px row. */}
    <div
      className="w-full rounded-sm bg-action-hover"
      style={{
        height:
          DATA_TABLE_ROW_HEIGHT[density ?? DEFAULT_DATA_TABLE_DENSITY] -
          PLACEHOLDER_BAR_VERTICAL_INSET_PX,
      }}
    />
  </div>
)

export type TableCellProps = HTMLAttributes<HTMLDivElement> & {
  ref?: Ref<HTMLDivElement>
  align?: DataTableAlign
  /**
   * Sticky-pin payload from `getPinnedCellProps(offsets, id, {variant:'body'})`.
   * Optional + additive. Pass the SAME column's payload the header uses so the
   * planes stay aligned; skeleton cells pass it too for the opaque background.
   */
  pinned?: PinnedCellProps
  /** 1-based visible-column index; emits `role="gridcell"` + `aria-colindex`. */
  colIndex?: number
  /**
   * Active cell in the `aria-activedescendant` model. Sets `data-active`, which the
   * CVA style turns into the ring + `relative` + the focus z-lift in ONE group, so
   * the positioning context and z-index can't be applied apart (z-index is a no-op
   * on `position: static`). NOT `:focus` — real DOM focus stays on the grid
   * container, so a `:focus` ring would never paint here.
   */
  active?: boolean
  /** `aria-selected`; emitted only when defined so an unselectable cell carries none. */
  selected?: boolean
}

const TableCell = ({
  active,
  align,
  className,
  colIndex,
  pinned,
  selected,
  style,
  ref,
  ...props
}: TableCellProps) => {
  const merged = mergePinned(pinned, style, className, dataTableCellVariants({ align }))

  const ariaProps = {
    ...(colIndex === undefined ? {} : { role: 'gridcell' as const, 'aria-colindex': colIndex }),
    ...(selected === undefined ? {} : { 'aria-selected': selected }),
    ...(active === true ? { 'data-active': 'true' as const } : {}),
  }

  return (
    <div
      ref={ref}
      className={merged.className}
      style={merged.style}
      {...merged.dataAttrs}
      {...ariaProps}
      {...props}
    />
  )
}

/**
 * Fixed width of the selection column (content-sized checkbox track, like MUI's
 * `__check__`). Shared so header + body selection cells match, and so the preset
 * can seed `getPinnedColumnOffsets`' left inset — that seeding is what makes a
 * left-pinned data column clear the (model-external) selection column instead of
 * colliding at `left: 0`.
 */
export const SELECTION_COLUMN_WIDTH_PX = 48

export type TableSelectionHeaderCellProps = Omit<TableHeaderCellProps, 'children'> & {
  checked: boolean
  indeterminate: boolean
  /** Toggle-all-loaded handler; `next` is the new checked value. */
  onCheckedChange: (next: boolean) => void
  /** Accessible name for the header checkbox — the app passes the translated string. */
  'aria-label': string
  disabled?: boolean
}

/** Select-all header cell: composes `TableHeaderCell` (inherits the pinned + columnheader seams) with the DS `Checkbox`. */
const TableSelectionHeaderCell = ({
  checked,
  indeterminate,
  onCheckedChange,
  disabled,
  align = 'center',
  'aria-label': ariaLabel,
  ...props
}: TableSelectionHeaderCellProps) => (
  <TableHeaderCell
    align={align}
    {...props}
  >
    {/*
      `small` (16px), deliberately unconditional and with no opt-out prop.
      The MUI theme sets `MuiCheckbox` defaultProps `size: 'small'` globally, so every checkbox
      in the DataGridPro tables this component replaces was small — `medium` was the design
      system diverging from the house style, not a choice any consumer made. A per-table
      override would let that divergence persist table by table, which is the thing being fixed.
      Every selection-enabled DataTable shifts 18px -> 16px with it; that is the intent.
    */}
    <Checkbox
      aria-label={ariaLabel}
      checked={checked}
      disabled={disabled}
      indeterminate={indeterminate}
      size="small"
      onCheckedChange={(value) => onCheckedChange(value)}
    />
  </TableHeaderCell>
)

export type TableSelectionCellProps = Omit<TableCellProps, 'children' | 'selected'> & {
  checked: boolean
  /** Toggle this row; `next` is the new checked value. */
  onCheckedChange: (next: boolean) => void
  /** Accessible name for the row checkbox — the app passes the translated string. */
  'aria-label': string
  disabled?: boolean
}

/**
 * Per-row selection cell: composes `TableCell` (inherits the pinned + gridcell
 * seams, sets `aria-selected`) with the DS `Checkbox`. `checked` comes from
 * `isRowSelected` — true under the exclusion model ("select all N matching"), so a
 * freshly loaded row renders checked.
 */
const TableSelectionCell = ({
  checked,
  onCheckedChange,
  disabled,
  align = 'center',
  'aria-label': ariaLabel,
  onClick,
  ...props
}: TableSelectionCellProps) => (
  <TableCell
    align={align}
    selected={checked}
    // Don't bubble to a row-level `onClick` — selecting a row is a distinct interaction.
    onClick={(event) => {
      event.stopPropagation()
      onClick?.(event)
    }}
    {...props}
  >
    <Checkbox
      aria-label={ariaLabel}
      checked={checked}
      disabled={disabled}
      size="small"
      onCheckedChange={(value) => onCheckedChange(value)}
    />
  </TableCell>
)

export {
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TablePlaceholderRow,
  TableRow,
  TableSelectionCell,
  TableSelectionHeaderCell,
}
